import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  useToast,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useColorModeValue,
  HStack,
  Divider
} from '@chakra-ui/react';
import { Task, CreateTaskData, UpdateTaskData, Duration, secondsToDuration, durationToSeconds, Reminder } from '../../../types/task';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { projectsAtom } from '../../../atoms/v0/projectAtoms';
import { addSeconds, format, parseISO } from 'date-fns';
import { DateQuickButtons } from './DateQuickButtons';
import { useTranslation } from 'react-i18next';
import { TagSelector } from '../tags/TagSelector';
import { taskTagsMapAtom, getTaskTagsAtom, addTagToTaskAtom, removeTagFromTaskAtom } from '../../../atoms/v0/taskTagAtoms';
import { RemindersEditor } from './RemindersEditor';

// --- Helper Function ---
// Memoized helper to calculate endAt
const calculateEndAt = (start: string, costInSeconds: number): string => {
  try {
    const startDate = parseISO(start);
    return addSeconds(startDate, costInSeconds).toISOString();
  } catch (error) {
    console.error("Error calculating end date:", error);
    return ''; // Return empty string or handle error appropriately
  }
};

// --- TaskForm Component Definition ---
export const TaskForm = ({
    task,
    onSubmit,
    isSubmitting,
    onCancel,
    initialData = {}, // 用于拖拽创建任务的初始数据
}: {
    task: Task | null;
    onSubmit: (data: CreateTaskData | UpdateTaskData) => Promise<void>;
    isSubmitting: boolean;
    onCancel: () => void;
    initialData?: Partial<CreateTaskData>;
}) => {
  const toast = useToast();
  const initialDataRef = useRef<Partial<CreateTaskData> | null>(initialData); // Allow null
  const [projects] = useAtom(projectsAtom);
  const { t } = useTranslation(['v0/tasks', 'v0/common']);

  // 标签相关的状态和操作
  const taskTagsMap = useAtomValue(taskTagsMapAtom);
  const fetchTaskTags = useSetAtom(getTaskTagsAtom);
  const addTagToTask = useSetAtom(addTagToTaskAtom);
  const removeTagFromTask = useSetAtom(removeTagFromTaskAtom);

  // --- State Initialization (Simplified) ---
  // Initialize formData based *only* on whether it's an edit or a basic new state
  const [formData, setFormData] = useState<CreateTaskData | UpdateTaskData>(() => {
    if (task) {
      // Initial state for editing (basic conversion)
      return {
        id: task.id,
        title: task.title,
        notes: task.notes,
        done: task.done,
        vital: task.vital,
        urgent: task.urgent,
        fixed: task.fixed,
        projectId: task.projectId,
        startAt: task.startAt || undefined,
        cost: task.cost || undefined,
        dueAt: task.dueAt || undefined,
        reminders: task.reminders || [],
      } as UpdateTaskData;
    } else {
      // Basic initial state for creating (will be overwritten by useEffect if initialData exists)
      return {
        title: '',
        notes: '',
        done: false,
        vital: false,
        urgent: false,
        fixed: false,
        projectId: undefined,
        startAt: undefined,
        cost: undefined,
        dueAt: undefined,
        reminders: [],
      } as CreateTaskData;
    }
  });

  // Initialize duration based on initial formData (which is derived from task or empty)
  const [duration, setDuration] = useState<Duration>(() => {
    const initialCost = task?.cost || undefined; // Only consider task's cost for initial duration
    if (initialCost && !isNaN(initialCost)) {
      return secondsToDuration(initialCost);
    }
    return { days: 0, hours: 0, minutes: 0 };
  });

  // --- Effect for Synchronization ---
  // Effect to synchronize form data ONLY when task prop changes or initialData is first applied
  useEffect(() => {
    let newData: CreateTaskData | UpdateTaskData;
    let newDuration: Duration;
    let isInitialDataSet = false;

    if (task) {
      // EDITING MODE: Always update from task prop
      newData = {
        id: task.id,
        title: task.title,
        notes: task.notes,
        done: task.done,
        vital: task.vital,
        urgent: task.urgent,
        fixed: task.fixed,
        projectId: task.projectId,
        startAt: task.startAt || undefined,
        cost: task.cost || undefined,
        dueAt: task.dueAt || undefined,
        reminders: task.reminders || [],
      } as UpdateTaskData;
      newDuration = task.cost ? secondsToDuration(task.cost) : { days: 0, hours: 0, minutes: 0 };

    } else if (initialData && initialDataRef.current === initialData) {
      // CREATING MODE - Apply initialData ONLY ONCE
      console.log("[TaskForm Effect] Applying initialData:", initialData);
      newData = {
        title: '', notes: '', done: false, vital: false, urgent: false, fixed: false,
        projectId: undefined, startAt: undefined, cost: undefined, dueAt: undefined,
        reminders: [],
        ...initialData
      } as CreateTaskData;
      newDuration = newData.cost ? secondsToDuration(newData.cost) : { days: 0, hours: 0, minutes: 0 };
      initialDataRef.current = null; // Mark initialData as applied
      isInitialDataSet = true;
    } else {
       // Neither editing nor applying initial data, do nothing in this effect
       return;
    }

    // Calculate endAt if possible
    if (newData.startAt && newData.cost) {
      newData.endAt = calculateEndAt(newData.startAt, newData.cost);
    }

    // Update state only if we determined new data (edit or initial create)
    setFormData(newData);
    setDuration(newDuration);
    if (isInitialDataSet) {
       console.log("[TaskForm Effect] State updated with initialData.");
    }

  // Re-run ONLY when the task prop changes (for editing)
  // or potentially when initialData reference changes (though we handle first application)
  }, [task]); // Removed initialData from dependencies

  // --- Handlers ---
  // Handler to update duration state AND form cost/endAt
  const handleDurationChange = (field: keyof Duration, value: number) => {
    if (isNaN(value)) {
      value = 0; // Prevent NaN
    }

    const newDuration = { ...duration, [field]: value };
    setDuration(newDuration);
    const newCost = durationToSeconds(newDuration);

    setFormData(prev => {
      const updatedData = { ...prev, cost: newCost };
      // Recalculate endAt if startAt exists
      if (updatedData.startAt) {
        updatedData.endAt = calculateEndAt(updatedData.startAt, newCost);
      }
      return updatedData;
    });
  };

  // Handler to update startAt AND form endAt
  const handleStartAtChange = (newStartAt: string | undefined) => {
    setFormData(prev => {
      const updatedData = { ...prev, startAt: newStartAt };
      // Recalculate endAt if cost exists
      if (newStartAt && updatedData.cost) {
        updatedData.endAt = calculateEndAt(newStartAt, updatedData.cost);
      }
      // If startAt is cleared, clear endAt too
      if (!newStartAt) {
         updatedData.endAt = undefined;
      }
      return updatedData;
    });
  };

  // Handler to update endAt AND form cost/duration
  const handleEndAtChange = (newEndAt: string | undefined) => {
     setFormData(prev => {
        const updatedData = { ...prev, endAt: newEndAt };
        // Recalculate cost and update duration if startAt exists
        if (prev.startAt && newEndAt) {
           try {
              const startDate = parseISO(prev.startAt);
              const endDate = parseISO(newEndAt);
              const newCost = Math.floor((endDate.getTime() - startDate.getTime()) / 1000);

              if (newCost >= 0) { // Allow zero duration
                 updatedData.cost = newCost;
                 // Update duration state outside of setFormData if possible,
                 // but for simplicity here, we'll update it based on the new cost after setting state.
                 // This might cause a slight delay in duration field update visually.
                 // A better approach might involve updating duration state here directly or using another useEffect.
                 setDuration(secondsToDuration(newCost));
              } else {
                 // Handle invalid end time (before start time)
                 updatedData.cost = undefined; // Or keep old cost? Set to 0?
                 setDuration({ days: 0, hours: 0, minutes: 0 });
              }
           } catch (error) {
              console.error("Error parsing dates for cost calculation:", error);
              updatedData.cost = undefined;
              setDuration({ days: 0, hours: 0, minutes: 0 });
           }
        } else {
          // If endAt is cleared or startAt doesn't exist, clear cost
          updatedData.cost = undefined;
          setDuration({ days: 0, hours: 0, minutes: 0 });
        }
        return updatedData;
     });
  };

  // 处理提醒变更
  const handleRemindersChange = (newReminders: Reminder[]) => {
    setFormData(prev => ({ ...prev, reminders: newReminders }));
  };

  // 如果有任务ID，加载该任务的标签
  useEffect(() => {
    if (task?.id) {
      fetchTaskTags(task.id);
    }
  }, [task?.id, fetchTaskTags]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const title = typeof formData.title === 'string' ? formData.title.trim() : '';
    if (!title) {
      toast({ title: t('v0/tasks:form.titleRequired'), status: 'warning', duration: 2000, isClosable: true, position: 'top' });
      return;
    }

    // Prepare data for submission, potentially removing the virtual 'endAt'
    const { endAt, ...dataToSubmit } = formData;

    onSubmit(task ? dataToSubmit as UpdateTaskData : dataToSubmit as CreateTaskData);
  };

  const formatISODateForInput = (isoDate?: string): string => {
    if (!isoDate) return '';
    try {
      // Ensure the date is valid before formatting
      const date = parseISO(isoDate);
      if (isNaN(date.getTime())) return '';
      return format(date, "yyyy-MM-dd'T'HH:mm");
    } catch (error) {
      console.error("Error formatting date for input:", error);
      return '';
    }
  };

  // --- Tag Handlers ---
  const handleAddTag = async (tagId: string) => {
    if (!task?.id) {
      toast({
        title: t('v0/tasks:tags.saveTaskFirstMessage'),
        description: t('v0/tasks:tags.saveTaskFirstDescription'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return;
    }

    const success = await addTagToTask({ taskId: task.id, tagId });
    if (!success) {
      toast({
        title: t('v0/tasks:tags.addTagFailedMessage'),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!task?.id) return;

    const success = await removeTagFromTask({ taskId: task.id, tagId });
    if (!success) {
      toast({
        title: t('v0/tasks:tags.removeTagFailedMessage'),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // --- Render ---
  return (
    <form onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        {/* Done Switch */}
        <FormControl display="flex" alignItems="center"> <Switch id="done" isChecked={!!formData.done} onChange={(e) => setFormData({ ...formData, done: e.target.checked })} colorScheme="green" mr={2} isDisabled={isSubmitting} /> <FormLabel htmlFor="done" mb={0}>{t('v0/common:common.completed', '已完成')}</FormLabel> </FormControl>

        {/* Title Input */}
        <FormControl isRequired> <FormLabel>{t('v0/tasks:form.titleField')}</FormLabel> <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder={t('v0/tasks:form.titleField')} size="lg" borderRadius="lg" autoFocus isDisabled={isSubmitting}/> </FormControl>

        {/* Tags Selector */}
        <FormControl>
          <FormLabel>{t('v0/tasks:tags.tagsLabel', '标签')}</FormLabel>
          <TagSelector
            selectedTags={task?.id ? (taskTagsMap[task.id] || []) : []}
            taskId={task?.id || ''}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            isDisabled={isSubmitting || !task}
          />
        </FormControl>

        {/* Notes Textarea */}
        <FormControl> <FormLabel>{t('v0/tasks:form.notesField')}</FormLabel> <Textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder={t('common:addNotesOrDescription', '添加备注或描述...')} minH="120px" borderRadius="lg" isDisabled={isSubmitting}/> </FormControl>

        {/* Project Select */}
        <FormControl> <FormLabel>{t('v0/tasks:form.projectField')}</FormLabel> <Select value={formData.projectId || ''} onChange={(e) => setFormData({ ...formData, projectId: e.target.value || undefined })} borderRadius="lg" isDisabled={isSubmitting}><option value="">{t('v0/tasks:form.noProject')}</option>{projects.map((project) => (<option key={project.id} value={project.id}>{project.title}</option>))}</Select> </FormControl>

        {/* Start Time Input */}
        <FormControl>
          <FormLabel>{t('v0/tasks:form.dates.startDate')}</FormLabel>
          <Input
            type="datetime-local"
            value={formatISODateForInput(formData.startAt)}
            onChange={(e) => {
              // Ensure value is valid or reset
              const value = e.target.value;
              try {
                const newStartAt = value ? new Date(value).toISOString() : undefined;
                handleStartAtChange(newStartAt);
              } catch {
                handleStartAtChange(undefined);
              }
            }}
            borderRadius="lg"
            isDisabled={isSubmitting}
          />
          <DateQuickButtons
            currentValue={formData.startAt}
            onChange={handleStartAtChange}
            isDisabled={isSubmitting}
          />
        </FormControl>

        {/* Duration Input */}
        <FormControl>
           <FormLabel>{t('v0/tasks:form.dates.duration', '时长')}</FormLabel>
           <HStack spacing={4}>
             <Box> <Text fontSize="sm" mb={1}>{t('v0/common:time.days', '天')}</Text> <NumberInput value={duration.days || 0} onChange={(_, value) => handleDurationChange('days', isNaN(value) ? 0 : value)} min={0} max={365} isDisabled={isSubmitting}> <NumberInputField borderRadius="lg"/> </NumberInput> </Box>
             <Box> <Text fontSize="sm" mb={1}>{t('v0/common:time.hours', '小时')}</Text> <NumberInput value={duration.hours || 0} onChange={(_, value) => handleDurationChange('hours', isNaN(value) ? 0 : value)} min={0} max={23} isDisabled={isSubmitting}> <NumberInputField borderRadius="lg"/> </NumberInput> </Box>
             <Box> <Text fontSize="sm" mb={1}>{t('v0/common:time.minutes', '分钟')}</Text> <NumberInput value={duration.minutes || 0} onChange={(_, value) => handleDurationChange('minutes', isNaN(value) ? 0 : value)} min={0} max={59} isDisabled={isSubmitting}> <NumberInputField borderRadius="lg"/> </NumberInput> </Box>
           </HStack>
        </FormControl>

        {/* End Time Input */}
        <FormControl>
          <FormLabel>{t('v0/common:time.endTime', '结束时间')}</FormLabel>
          <Input
            type="datetime-local"
            value={formatISODateForInput(formData.endAt)}
            onChange={(e) => {
              const value = e.target.value;
              try {
                 const newEndAt = value ? new Date(value).toISOString() : undefined;
                 handleEndAtChange(newEndAt);
              } catch {
                 handleEndAtChange(undefined);
              }
            }}
            borderRadius="lg"
            isDisabled={isSubmitting || !formData.startAt} // Disable if no start time
          />
        </FormControl>

        {/* Due Date Input */}
        <FormControl>
          <FormLabel>{t('v0/tasks:form.dates.dueDate')}</FormLabel>
          <Input
            type="datetime-local"
            value={formatISODateForInput(formData.dueAt)}
            onChange={(e) => {
              const value = e.target.value;
              try {
                const newDueAt = value ? new Date(value).toISOString() : undefined;
                setFormData({ ...formData, dueAt: newDueAt }); // Directly set due date
              } catch {
                 setFormData({ ...formData, dueAt: undefined });
              }
            }}
            borderRadius="lg"
            isDisabled={isSubmitting}
          />
          <DateQuickButtons
            currentValue={formData.dueAt}
            onChange={(newDueAt) => setFormData({ ...formData, dueAt: newDueAt })}
            isDisabled={isSubmitting}
          />
        </FormControl>

        {/* 提醒设置 */}
        <FormControl>
          <RemindersEditor
            reminders={formData.reminders || []}
            onChange={handleRemindersChange}
            isDisabled={isSubmitting}
          />
        </FormControl>

        {/* Switches (Vital, Urgent, Fixed) */}
        <Flex gap={6} mt={2}>
           <FormControl display="flex" alignItems="center"> <Switch id="vital" isChecked={!!formData.vital} onChange={(e) => setFormData({ ...formData, vital: e.target.checked })} colorScheme="red" mr={2} isDisabled={isSubmitting}/> <FormLabel htmlFor="vital" mb={0}>{t('v0/tasks:form.priority.vital')}</FormLabel> </FormControl>
           <FormControl display="flex" alignItems="center"> <Switch id="urgent" isChecked={!!formData.urgent} onChange={(e) => setFormData({ ...formData, urgent: e.target.checked })} colorScheme="orange" mr={2} isDisabled={isSubmitting}/> <FormLabel htmlFor="urgent" mb={0}>{t('v0/tasks:form.priority.urgent')}</FormLabel> </FormControl>
           <FormControl display="flex" alignItems="center"> <Switch id="fixed" isChecked={!!formData.fixed} onChange={(e) => setFormData({ ...formData, fixed: e.target.checked })} colorScheme="blue" mr={2} isDisabled={isSubmitting}/> <FormLabel htmlFor="fixed" mb={0}>{t('v0/tasks:form.priority.fixed')}</FormLabel> </FormControl>
        </Flex>

        {/* Submit Button */}
        <Button type="submit" colorScheme="pink" size="lg" w="100%" mt={4} borderRadius="xl" _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }} isLoading={isSubmitting} loadingText={task ? t('v0/common:updating', '更新中...') : t('v0/common:creating', '创建中...')}>{task ? t('v0/tasks:form.editTitle') : t('v0/tasks:form.createTitle')}</Button>
      </VStack>
    </form>
  );
};